import React from "react";

import { Globe } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";

import { useChatContext } from "../../contexts/useChatContext";

import type { AskNeedlTools } from "@/types/api";

interface WebSearchToggleProps {
  disabled?: boolean;
}

const WebSearchToggle: React.FC<WebSearchToggleProps> = ({
  disabled = false,
}) => {
  const { tools, isLoading, isStreaming, setTools } = useChatContext();

  const webSearchTool = tools?.find(
    (t) => t.id === "web_search" && t.type === "toggle"
  );
  const webSearchEnabled =
    webSearchTool?.type === "toggle" ? webSearchTool.enabled : false;

  const isDisabled = disabled || isLoading || isStreaming || !tools;

  const toggle = () => {
    if (!tools) return;
    const updated: AskNeedlTools = tools.map((t) =>
      t.id === "web_search" && t.type === "toggle"
        ? { ...t, enabled: !t.enabled }
        : t
    );
    setTools(updated);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex" tabIndex={isDisabled ? 0 : undefined}>
          <button
            type="button"
            onClick={toggle}
            disabled={isDisabled}
            aria-pressed={webSearchEnabled}
            aria-label="Toggle web search"
            data-testid="web-search-toggle-button"
            className={`p-1.5 rounded-md transition-all duration-200 flex items-center justify-center gap-1.5 text-sm ${
              isDisabled
                ? "opacity-50 cursor-not-allowed text-gray-400"
                : webSearchEnabled
                  ? "text-primary bg-primary/10"
                  : "text-gray-600 hover:text-gray-700 hover:bg-gray-50 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-700"
            }`}
          >
            <Globe size={16} />
            <span className="font-medium">Web</span>
          </button>
        </span>
      </TooltipTrigger>
      <TooltipContent className="z-[99999]">
        <p>
          {isDisabled
            ? "Web search unavailable"
            : webSearchEnabled
              ? "Web search on"
              : "Web search off"}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};

export default WebSearchToggle;
